import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faDownload, 
  faShare, 
  faChevronLeft, 
  faChevronRight,
  faMusic,
  faVideo,
  faQuoteLeft,
  faCalendarAlt,
  faChartLine,
  faEnvelope,
  faPhone,
  faGlobe,
  faUser,
  faArrowLeft,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { 
  faSpotify, 
  faApple, 
  faSoundcloud, 
  faYoutube, 
  faInstagram, 
  faFacebook, 
  faTwitter, 
  faTiktok,
  faLinkedin
} from '@fortawesome/free-brands-svg-icons';
import html2pdf from 'html2pdf.js';
import styles from './EPKViewer.module.css';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { firebaseInstances } from '../../utils/firebaseHelper';
import logo from '../../assets/Images/logo.png'

const EPKPage = () => {
  const { username, epkId } = useParams();
  const navigate = useNavigate();
  const [epkData, setEpkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Ref for PDF generation
  const epkRef = React.useRef(null);
  
  // Check if device is mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch EPK data
  useEffect(() => {
    const fetchEPK = async () => {
      setLoading(true);
      try {
        const db = firebaseInstances.firestore;
        
        // First try to find the user by username
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('userName', '==', username), limit(1));
        const userSnapshot = await getDocs(q);
        
        if (userSnapshot.empty) {
          throw new Error('Artist not found');
        }
        
        const userData = userSnapshot.docs[0].data();
        const userId = userSnapshot.docs[0].id;
        
        // If epkId is provided, get that specific EPK, otherwise get the user's active EPK
        let epkDoc;
        if (epkId) {
          const epkDocRef = doc(db, 'users', userId, 'epk', epkId);
          epkDoc = await getDoc(epkDocRef);
          
          if (!epkDoc.exists()) {
            throw new Error('EPK not found');
          }
        } else {
          // Get the active EPK
          const epkCollectionRef = collection(db, `users/${userId}/epk`);
          const activeEpkQuery = query(epkCollectionRef, where('isActive', '==', true), limit(1));
          const activeEpkSnapshot = await getDocs(activeEpkQuery);
          
          if (activeEpkSnapshot.empty) {
            throw new Error('No active EPK found');
          }
          
          epkDoc = activeEpkSnapshot.docs[0];
        }
        
        // Set the EPK data
        setEpkData({
          ...epkDoc.data(),
          id: epkDoc.id,
          userId,
          userName: userData.userName,
          userType: userData.userType
        });
      } catch (err) {
        console.error('Error fetching EPK:', err);
        setError(err.message || 'Failed to load EPK');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEPK();
  }, [username, epkId]);
  
  // Handle image navigation
  const goToNextPhoto = () => {
    if (!epkData || !epkData.photos || epkData.photos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => (prevIndex + 1) % epkData.photos.length);
  };
  
  const goToPrevPhoto = () => {
    if (!epkData || !epkData.photos || epkData.photos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => (prevIndex - 1 + epkData.photos.length) % epkData.photos.length);
  };
  
  // Handle sharing EPK
  const handleShareEPK = () => {
    const url = window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: `${epkData.artistName} - Electronic Press Kit`,
        text: epkData.shortBio || `Check out ${epkData.artistName}'s Electronic Press Kit`,
        url: url
      }).catch(err => {
        console.error('Error sharing:', err);
        navigator.clipboard.writeText(url);
        alert('EPK link copied to clipboard!');
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('EPK link copied to clipboard!');
    }
  };
  
  // Handle PDF generation
  const handleExportPDF = async () => {
    if (isGeneratingPDF) return;
    
    setIsGeneratingPDF(true);
    try {
      const element = epkRef.current;
      const pdfOptions = {
        margin: 10,
        filename: `${epkData.artistName || 'Artist'}_EPK.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      // Generate the PDF
      await html2pdf().from(element).set(pdfOptions).save();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  
  // Function to determine if a color is dark
  const isColorDark = (hexColor) => {
    if (!hexColor) return false;
    
    let r = 0, g = 0, b = 0;
    
    if (hexColor.length === 4) {
      r = parseInt(hexColor[1] + hexColor[1], 16);
      g = parseInt(hexColor[2] + hexColor[2], 16);
      b = parseInt(hexColor[3] + hexColor[3], 16);
    } else if (hexColor.length === 7) {
      r = parseInt(hexColor.substring(1, 3), 16);
      g = parseInt(hexColor.substring(3, 5), 16);
      b = parseInt(hexColor.substring(5, 7), 16);
    }
    
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  };
  
  // Function to format location
  const formatLocation = () => {
    if (!epkData || !epkData.location) return '';
    
    const parts = [];
    if (epkData.location.city) parts.push(epkData.location.city);
    if (epkData.location.state) parts.push(epkData.location.state);
    if (epkData.location.country) parts.push(epkData.location.country);
    return parts.join(', ');
  };
  
  // Format numbers for stats display
  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat().format(num);
  };
  
  // Handle navigation to artist profile
  const handleGoToProfile = () => {
    navigate(`/${username}`);
  };
  
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading EPK...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} className={styles.backButton}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
        </button>
      </div>
    );
  }
  
  if (!epkData) {
    return (
      <div className={styles.errorContainer}>
        <h2>EPK Not Found</h2>
        <p>The requested EPK could not be found.</p>
        <button onClick={() => navigate('/')} className={styles.backButton}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
        </button>
      </div>
    );
  }
  
  // Determine if background is dark
  const isDarkBackground = isColorDark(epkData.theme?.backgroundColor);
  
  // Get theme-specific class name
  const getThemeClassName = () => {
    const baseClass = styles.epkViewer;
    const themeTemplate = epkData.theme?.template || 'minimal';
    
    let themeClass = '';
    if (themeTemplate === 'visual') {
      themeClass = styles.visualTheme;
    } else if (themeTemplate === 'classic') {
      themeClass = styles.classicTheme;
    } else {
      themeClass = styles.minimalTheme;
    }
    
    return `${baseClass} ${themeClass} ${isDarkBackground ? styles.darkText : styles.lightText}`;
  };
  
  return (
    <div className={styles.epkPageContainer}>
      {/* Profile navigation header */}
      <div className={styles.epkPageHeader} style={{
        backgroundColor: isDarkBackground ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        color: isDarkBackground ? '#fff' : '#333'
      }}>
        <div className={styles.headerLeft}>
          <img 
            src={logo} 
            alt="Trillio Logo" 
            className={styles.headerLogo} 
          />
          <button onClick={handleGoToProfile} className={styles.profileButton}>
            <FontAwesomeIcon icon={faUser} />
            <span>View Profile</span>
          </button>
        </div>
        
        <div className={styles.epkActions}>
         {/* <button
            className={styles.epkActionBtn}
            onClick={handleExportPDF}
            title="Export as PDF"
            disabled={isGeneratingPDF}
          >
            <FontAwesomeIcon icon={isGeneratingPDF ? faSpinner : faDownload} spin={isGeneratingPDF} />
          </button> */}
          
          <button
            className={styles.epkActionBtn}
            onClick={handleShareEPK}
            title="Share EPK"
          >
            <FontAwesomeIcon icon={faShare} />
          </button>
        </div>
      </div>
      
      {/* Main EPK Content */}
      <div 
        ref={epkRef}
        className={getThemeClassName()}
        style={{ 
          '--accent-color': epkData.theme?.accentColor || '#E2384D',
          '--background-color': epkData.theme?.backgroundColor || '#FFFFFF',
          backgroundColor: epkData.theme?.backgroundColor || '#FFFFFF',
          color: isDarkBackground ? '#FFFFFF' : '#333333'
        }}
      >
        <div className={styles.epkContent}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            {epkData.logo && (
              <div className={styles.logoContainer}>
                <img src={epkData.logo.url} alt={`${epkData.artistName} logo`} className={styles.artistLogo} />
              </div>
            )}
            
            <h2 className={styles.artistName}>{epkData.artistName}</h2>
            {epkData.artistType && <h3 className={styles.artistType}>{epkData.artistType}</h3>}
            
            <div className={styles.artistDetails}>
              {formatLocation() && (
                <div className={styles.artistLocation}>
                  {formatLocation()}
                </div>
              )}
              
              {epkData.genres && epkData.genres.length > 0 && (
                <div className={styles.artistGenres}>
                  {epkData.genres.join(' • ')}
                </div>
              )}
              
              {epkData.activeSince && (
                <div className={styles.artistSince}>
                  Est. {epkData.activeSince}
                </div>
              )}
            </div>
            
            {epkData.shortBio && (
              <div className={styles.artistShortBio}>
                {epkData.shortBio}
              </div>
            )}
            
            {/* Social Media Links */}
            <div className={styles.socialLinks}>
              {epkData.socialLinks?.spotify && (
                <a href={epkData.socialLinks.spotify} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faSpotify} />
                </a>
              )}
              
              {epkData.socialLinks?.appleMusic && (
                <a href={epkData.socialLinks.appleMusic} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faApple} />
                </a>
              )}
              
              {epkData.socialLinks?.instagram && (
                <a href={epkData.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
              )}
              
              {epkData.socialLinks?.facebook && (
                <a href={epkData.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faFacebook} />
                </a>
              )}
              
              {epkData.socialLinks?.twitter && (
                <a href={epkData.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faTwitter} />
                </a>
              )}
              
              {epkData.socialLinks?.tiktok && (
                <a href={epkData.socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faTiktok} />
                </a>
              )}
              
              {epkData.socialLinks?.youtube && (
                <a href={epkData.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faYoutube} />
                </a>
              )}
              
              {epkData.socialLinks?.soundcloud && (
                <a href={epkData.socialLinks.soundcloud} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faSoundcloud} />
                </a>
              )}
              
              {epkData.socialLinks?.linkedin && (
                <a href={epkData.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <FontAwesomeIcon icon={faLinkedin} />
                </a>
              )}
            </div>
          </section>
          
          {/* Photos Section */}
          {epkData.photos && epkData.photos.length > 0 && (
            <section className={styles.photosSection}>
              <h3 className={styles.sectionTitle}>
                Photos
              </h3>
              
              <div className={styles.photoSlider}>
                <div className={styles.mainPhoto}>
                  <img 
                    src={epkData.photos[currentPhotoIndex].url} 
                    alt={`${epkData.artistName} promo ${currentPhotoIndex + 1}`} 
                  />
                  
                  {epkData.photos.length > 1 && (
                    <>
                      <button 
                        className={`${styles.photoNavBtn} ${styles.photoNavBtnPrev}`}
                        onClick={goToPrevPhoto}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      
                      <button 
                        className={`${styles.photoNavBtn} ${styles.photoNavBtnNext}`}
                        onClick={goToNextPhoto}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                      
                      <div className={styles.photoCounter}>
                        {currentPhotoIndex + 1} / {epkData.photos.length}
                      </div>
                    </>
                  )}
                </div>
                
                {epkData.photos.length > 1 && (
                  <div className={styles.photoThumbnails}>
                    {epkData.photos.map((photo, index) => (
                      <div 
                        key={photo.id || index}
                        className={`${styles.photoThumb} ${index === currentPhotoIndex ? styles.activeThumb : ''}`}
                        onClick={() => setCurrentPhotoIndex(index)}
                      >
                        <img src={photo.thumbnailUrl || photo.url} alt={`Thumbnail ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}
          
          {/* Bio Section */}
          {epkData.longBio && (
            <section className={styles.bioSection}>
              <h3 className={styles.sectionTitle}>Biography</h3>
              <div className={styles.bioContent}>
                {epkData.longBio}
              </div>
            </section>
          )}
          
          {/* Music Section */}
          {((epkData.music?.spotify && epkData.music.spotify.length > 0) || 
            (epkData.music?.appleMusic && epkData.music.appleMusic.length > 0) || 
            (epkData.music?.soundcloud && epkData.music.soundcloud.length > 0)) && (
            <section className={styles.musicSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faMusic} />
                Music
              </h3>
              
              <div className={styles.musicEmbeds}>
                {epkData.music?.spotify && epkData.music.spotify.map((track, index) => (
                  <div key={`spotify-${index}`} className={styles.musicEmbed}>
                    <iframe 
                      src={`https://open.spotify.com/embed/track/${track.id}`}
                      width="100%" 
                      height="80" 
                      frameBorder="0" 
                      allowtransparency="true" 
                      allow="encrypted-media"
                      title={`Spotify Track ${index + 1}`}
                    ></iframe>
                  </div>
                ))}
                
                {epkData.music?.appleMusic && epkData.music.appleMusic.map((track, index) => (
                  <div key={`apple-${index}`} className={styles.musicEmbed}>
                    <iframe 
                      allow="autoplay *; encrypted-media *; fullscreen *" 
                      frameBorder="0" 
                      height="150" 
                      width="100%" 
                      sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" 
                      src={track.embedUrl}
                      title={`Apple Music Track ${index + 1}`}
                    ></iframe>
                  </div>
                ))}
                
                {epkData.music?.soundcloud && epkData.music.soundcloud.map((track, index) => (
                  <div key={`soundcloud-${index}`} className={styles.musicEmbed}>
                    <iframe 
                      width="100%" 
                      height="166" 
                      scrolling="no" 
                      frameBorder="0" 
                      src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(track.url)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                      title={`SoundCloud Track ${index + 1}`}
                    ></iframe>
                  </div>
                ))}
              </div>
            </section>
          )}
          
          {/* Videos Section */}
          {epkData.videos && epkData.videos.length > 0 && (
            <section className={styles.videoSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faVideo} />
                Videos
              </h3>
              
              <div className={styles.videoGrid}>
                {epkData.videos.map((video, index) => (
                  <div key={`video-${index}`} className={styles.videoEmbed}>
                    <iframe 
                      width="100%" 
                      height="315" 
                      src={video.embedUrl} 
                      title={video.title || `Video ${index + 1}`}
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                    
                    {video.title && (
                      <h4 className={styles.videoTitle}>{video.title}</h4>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          
          {/* Performances Section */}
          {epkData.performances && epkData.performances.length > 0 && (
            <section className={styles.performancesSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faCalendarAlt} />
                Performance History
              </h3>
              
              <div className={styles.performancesGrid}>
                {epkData.performances.map((performance, index) => (
                  <div key={`perf-${index}`} className={styles.performanceCard}>
                    <h4 className={styles.perfTitle}>{performance.name}</h4>
                    
                    <div className={styles.perfDetails}>
                      <span className={styles.perfYear}>{performance.year}</span>
                      {performance.city && (
                        <span className={styles.perfCity}>{performance.city}</span>
                      )}
                    </div>
                    
                    {performance.isHeadliner && (
                      <div className={styles.perfHeadliner}>Headliner</div>
                    )}
                    
                    {performance.audienceSize && (
                      <div className={styles.perfAudience}>
                        Audience: {formatNumber(performance.audienceSize)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          
          {/* Stats Section */}
          {epkData.stats && (
            <section className={styles.statsSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faChartLine} />
                Stats
              </h3>
              
              <div className={styles.statsGrid}>
                {epkData.stats.spotify?.monthlyListeners && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faSpotify} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(epkData.stats.spotify.monthlyListeners)}
                    </div>
                    <div className={styles.statLabel}>
                      Monthly Listeners
                    </div>
                  </div>
                )}
                
                {epkData.stats.spotify?.totalStreams && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faSpotify} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(epkData.stats.spotify.totalStreams)}
                    </div>
                    <div className={styles.statLabel}>
                      Total Streams
                    </div>
                  </div>
                )}
                
                {epkData.stats.youtube?.totalViews && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faYoutube} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(epkData.stats.youtube.totalViews)}
                    </div>
                    <div className={styles.statLabel}>
                      YouTube Views
                    </div>
                  </div>
                )}
                
                {epkData.stats.instagram && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faInstagram} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(epkData.stats.instagram)}
                    </div>
                    <div className={styles.statLabel}>
                      Instagram Followers
                    </div>
                  </div>
                )}
                
                {epkData.stats.tiktok && (
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FontAwesomeIcon icon={faTiktok} />
                    </div>
                    <div className={styles.statValue}>
                      {formatNumber(epkData.stats.tiktok)}
                    </div>
                    <div className={styles.statLabel}>
                      TikTok Followers
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
          
          {/* Press Quotes Section */}
          {epkData.pressQuotes && epkData.pressQuotes.length > 0 && (
            <section className={styles.pressSection}>
              <h3 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faQuoteLeft} />
                Press
              </h3>
              
              <div className={styles.pressQuotes}>
                {epkData.pressQuotes.map((quote, index) => (
                  <div key={`quote-${index}`} className={styles.quoteCard}>
                    <div className={styles.quoteText}>
                      "{quote.quote}"
                    </div>
                    <div className={styles.quoteSource}>
                      — {quote.source}
                      {quote.link && (
                        <a 
                          href={quote.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.quoteLink}
                        >
                          Read More
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {epkData.pressCoverage && epkData.pressCoverage.length > 0 && (
                <div className={styles.pressCoverage}>
                  <h4 className={styles.pressCoverageTitle}>Media Coverage</h4>
                  <ul className={styles.pressCoverageList}>
                    {epkData.pressCoverage.map((coverage, index) => (
                      <li key={`coverage-${index}`} className={styles.pressCoverageItem}>
                        <a 
                          href={coverage.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.coverageLink}
                        >
                          {coverage.title} — {coverage.publication}
                          {coverage.date && ` (${coverage.date})`}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}
          
          {/* Contact Section */}
          {epkData.contact && (
            <section className={styles.contactSection}>
              <h3 className={styles.sectionTitle}>
                Contact
              </h3>
              
              <div className={styles.contactGrid}>
                {epkData.contact.name && epkData.contact.role && (
                  <div className={styles.contactPerson}>
                    <div className={styles.contactName}>{epkData.contact.name}</div>
                    <div className={styles.contactRole}>{epkData.contact.role}</div>
                  </div>
                )}
                
                <div className={styles.contactInfo}>
                  {epkData.contact.email && (
                    <div className={styles.contactItem}>
                      <FontAwesomeIcon icon={faEnvelope} className={styles.contactIcon} />
                      <a href={`mailto:${epkData.contact.email}`} className={styles.contactLink}>
                        {epkData.contact.email}
                      </a>
                    </div>
                  )}
                  
                  {epkData.contact.phone && (
                    <div className={styles.contactItem}>
                      <FontAwesomeIcon icon={faPhone} className={styles.contactIcon} />
                      <a href={`tel:${epkData.contact.phone}`} className={styles.contactLink}>
                        {epkData.contact.phone}
                      </a>
                    </div>
                  )}
                  
                  {epkData.contact.website && (
                    <div className={styles.contactItem}>
                      <FontAwesomeIcon icon={faGlobe} className={styles.contactIcon} />
                      <a 
                        href={epkData.contact.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.contactLink}
                      >
                        {epkData.contact.website.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>
        
        <div className={styles.epkFooter}>
          <div className={styles.footerContent}>
            <div className={styles.epkPoweredBy}>
              Powered by OnTour EPK Builder
            </div>
            <div className={styles.epkTimestamp}>
              Last updated: {new Date(epkData.updatedAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EPKPage;